import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ChevronLeft, Settings, CircleUser, Camera, Pencil, CalendarDays, Crown,
  ChevronDown, Gem, Gift, Rocket, Headphones, X, Mail, Phone, Lock, LogOut,
  Sparkles, Star, Zap, Trophy, Heart, Award, Medal, ShieldCheck, BadgeCheck,
  Coins, DollarSign, Clock, Flame, Copy,
} from 'lucide-react';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { phoneVerificationReward } from '../../../utils/phoneVerificationReward';
import { renderInlineMarkdown, parseBenefit } from '../../../lib/inlineMarkdown';
import { tap } from './playerHelpers';
import SafeImage from '../../../components/SafeImage';

/* -------------------- BENEFIT ICON SHORTCODES (Iter 145+) --------------------
 * Admins can prefix a benefit with `[shortcode]` (e.g. "[gift] 10% deposit
 * bonus") to render a polished lucide icon instead of an emoji or bullet.
 * Add new mappings here as needed — they're rendered by `BenefitRow` below.
 */
const BENEFIT_ICONS = {
  gift: Gift, sparkles: Sparkles, star: Star, zap: Zap, trophy: Trophy,
  heart: Heart, award: Award, medal: Medal, shield: ShieldCheck, verified: BadgeCheck,
  crown: Crown, gem: Gem, rocket: Rocket, support: Headphones, headphones: Headphones,
  coins: Coins, dollar: DollarSign, money: DollarSign, clock: Clock,
  fast: Zap, priority: Zap, flame: Flame, fire: Flame,
};

const PROFILE_ACCENT = 'var(--vl-profile-accent, var(--vl-neon, #004D61))';
const PROFILE_ACCENT_TEXT = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 70%, #FFFFFF)';
const PROFILE_ACCENT_DIM = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 16%, transparent)';
const PROFILE_ACCENT_SOFT = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 24%, transparent)';
const PROFILE_ACCENT_BORDER = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 46%, transparent)';
const PROFILE_ACCENT_STRONG_BORDER = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 72%, transparent)';
const PROFILE_ACCENT_GLOW = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 42%, transparent)';
const PROFILE_ACCENT_GRADIENT = 'linear-gradient(135deg, var(--vl-btn-primary-from, var(--vl-profile-accent, var(--vl-neon, #004D61))), var(--vl-btn-primary-to, #004D61))';
const PROFILE_CARD_GRADIENT = 'linear-gradient(135deg, color-mix(in oklab, var(--vl-bg, #000000) 76%, var(--vl-profile-accent, var(--vl-neon, #004D61))), color-mix(in oklab, var(--vl-bg, #000000) 91%, var(--vl-profile-accent, var(--vl-neon, #004D61))))';
const PROFILE_ACTION_BG = 'color-mix(in oklab, var(--vl-bg, #000000) 88%, #FFFFFF 12%)';
const PROFILE_ACTION_BORDER = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 42%, #FFFFFF 18%)';
const PROFILE_GOLD = 'var(--vl-brand-primary, #D4AF37)';

const defaultNameFromEmail = (value = '') => {
  const prefix = String(value || '').split('@')[0] || '';
  const first = prefix.match(/[a-z0-9]/i)?.[0] || '';
  return first ? first.toUpperCase() : '';
};

function BenefitRow({ raw, accent = PROFILE_ACCENT }) {
  const { icon, text } = parseBenefit(raw);
  // Resolve a lucide icon when admin used `[shortcode]`.
  let LucideIcon = null;
  if (icon && icon.startsWith('[') && icon.endsWith(']')) {
    LucideIcon = BENEFIT_ICONS[icon.slice(1, -1).toLowerCase()];
  }
  return (
    <div
      className="flex items-center gap-3 px-3 py-2 rounded-xl"
      style={{ background: `color-mix(in oklab, ${accent} 10%, transparent)`, border: `1px solid color-mix(in oklab, ${accent} 25%, transparent)` }}
    >
      <span
        className="w-8 h-8 rounded-lg flex items-center justify-center text-base shrink-0"
        style={{ background: `color-mix(in oklab, ${accent} 20%, transparent)`, color: accent }}
        aria-hidden
      >
        {LucideIcon ? <LucideIcon className="w-4 h-4" /> : (icon || '•')}
      </span>
      <span className="text-sm leading-snug" style={{ color: '#E5E7EB' }}>{text}</span>
    </div>
  );
}

/* -------------------- TIER BENEFIT BADGE -------------------- */
function TierBenefit({ icon: Icon, label, color = PROFILE_ACCENT }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid color-mix(in oklab, ${color} 34%, transparent)`, boxShadow: `0 0 14px color-mix(in oklab, ${color} 20%, transparent)` }}
      >
        <Icon className="w-5 h-5" style={{ color }} strokeWidth={2.2} />
      </span>
      <p className="text-[10px] font-semibold leading-tight text-center" style={{ color: PROFILE_ACCENT_TEXT }}>{label}</p>
    </div>
  );
}

/* -------------------- SETTINGS CARD (used in Security & Account) -------------------- */
function SettingsCard({ icon: Icon, iconBg, iconClassName = 'w-5 h-5', title, subtitle, onClick, testid }) {
  return (
    <button
      onClick={() => { tap(); onClick?.(); }}
      className="w-full flex items-center gap-4 rounded-2xl px-4 py-4 mt-3 transition active:scale-[0.99] hover:brightness-110 text-left"
      style={{
        background: PROFILE_ACCENT_DIM,
        border: `1px solid ${PROFILE_ACCENT_BORDER}`,
        boxShadow: `0 0 18px ${PROFILE_ACCENT_GLOW}`,
      }}
      data-testid={testid}
    >
      <span
        className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
        style={{
          background: iconBg || PROFILE_ACCENT_GRADIENT,
          border: '1.5px solid rgba(255,255,255,0.36)',
          outline: `1px solid ${PROFILE_ACCENT_STRONG_BORDER}`,
          outlineOffset: '2px',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18)',
        }}
        aria-hidden
      >
        <Icon className={iconClassName} style={{ color: '#FFFFFF' }} strokeWidth={2.35} />
      </span>
      <div className="flex-1 min-w-0">
        <p
          className="font-extrabold text-base sm:text-lg leading-tight"
          style={{ color: '#FFFFFF', fontFamily: '"Plus Jakarta Sans", "Inter", system-ui, sans-serif' }}
        >
          {title}
        </p>
        <p
          className="text-sm leading-tight mt-0.5 truncate"
          style={{ color: 'rgba(255,255,255,0.55)' }}
        >
          {subtitle}
        </p>
      </div>
      <ChevronDown className="w-5 h-5 -rotate-90 shrink-0" style={{ color: PROFILE_ACCENT_TEXT }} />
    </button>
  );
}

function VerifiedUserBadge({ size = 'normal', className = '' }) {
  const dims = size === 'small' ? 'w-4 h-4' : 'w-5 h-5';
  const iconSize = size === 'small' ? 10 : 12;
  return (
    <span
      className={`${dims} rounded-full inline-flex items-center justify-center shrink-0 ${className}`}
      style={{
        background: 'radial-gradient(circle at 30% 25%, #FFF4B8 0%, var(--vl-brand-primary, #D4AF37) 48%, #8B5E00 100%)',
        border: '2px solid #0B0F0C',
        boxShadow: '0 0 8px color-mix(in oklab, var(--vl-brand-primary, #D4AF37) 72%, transparent), inset 0 1px 1px rgba(255,255,255,0.55)',
      }}
      aria-hidden="true"
    >
      <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 6L9 17l-5-5" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

/* -------------------- PROFILE FULL-SCREEN -------------------- */
export default function ProfileFullScreen({
  user,
  branding,
  badge,
  settingsOpen = false,
  onClose,
  onOpenSettings,
  onCloseSettings,
  onEditProfile,
  onEditPhone,
  onResetPassword,
  onLogout,
}) {
  const { checkAuth } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);
  // Iter 145: admin-edited tier descriptions surfaced when player taps
  // a card in the All-Tiers modal. Map keyed by lowercased tier name.
  const [tierDocs, setTierDocs] = useState({});
  const [tierDetail, setTierDetail] = useState(null); // currently-shown tier in detail modal

  useEffect(() => {
    api.get('/api/referrals/stats')
      .then(r => { if (r.data) setStats(r.data); })
      .catch(() => {});
    // Fetch tier docs (admin-edited descriptions, perks, benefits)
    api.get('/api/tiers')
      .then(r => {
        const list = r.data?.tiers || r.data || [];
        const map = {};
        list.forEach((t) => {
          const k = (t.name || '').toLowerCase();
          if (k) map[k] = t;
        });
        setTierDocs(map);
      })
      .catch(() => {});
  }, []);

  // Removed duplicate useEffect above (keeps single source of truth)
  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max image size is 5 MB'); return; }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            const max = 320;
            const scale = Math.min(1, max / Math.max(img.width, img.height));
            const w = Math.round(img.width * scale);
            const h = Math.round(img.height * scale);
            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL('image/jpeg', 0.85));
          };
          img.onerror = reject;
          img.src = reader.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.patch('/api/auth/profile', { avatar_url: dataUrl });
      await checkAuth();
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    } finally { setUploading(false); }
  };

  const avatarUrl = user?.avatar_url;

  // Tier ladder (display order from bottom -> top)
  const TIERS = [
    { key: 'starter',   name: 'Starter',   level: 1, pointsToNext: 1000,  gradient: 'linear-gradient(135deg, #9CA3AF, #4B5563)', crystal: '#9CA3AF', icon: '🎖️' },
    { key: 'bronze',    name: 'Bronze',    level: 2, pointsToNext: 3000,  gradient: 'linear-gradient(135deg, #CD7F32, #7C4A1E)', crystal: '#CD7F32', icon: '🥉' },
    { key: 'silver',    name: 'Silver',    level: 3, pointsToNext: 5000,  gradient: 'linear-gradient(135deg, #E5E7EB, #9CA3AF)', crystal: '#E5E7EB', icon: '🥈' },
    { key: 'gold',      name: 'Gold',      level: 4, pointsToNext: 10000, gradient: 'linear-gradient(135deg, #FCD34D, #D97706)', crystal: '#FBBF24', icon: '🥇' },
    { key: 'diamond',   name: 'Diamond',   level: 5, pointsToNext: 25000, gradient: 'linear-gradient(135deg, #B8E6EE, #004D61)', crystal: '#B8E6EE', icon: '💎' },
    { key: 'platinum',  name: 'Platinum',  level: 6, pointsToNext: 50000, gradient: 'linear-gradient(135deg, #22D3EE, #0E7490)', crystal: '#22D3EE', icon: '✨' },
    { key: 'elite',     name: 'Elite',     level: 7, pointsToNext: null,  gradient: 'linear-gradient(135deg, #F97316, #DC2626)', crystal: '#F97316', icon: '👑' },
  ];
  const currentTierKey = (user?.achievement_tier || user?.tier || 'diamond').toString().toLowerCase();
  const currentTier = TIERS.find(t => t.key === currentTierKey) || TIERS[4];
  const currentPoints = user?.tier_points ?? stats?.tier_points ?? 6250;
  const pointsToNext = currentTier.pointsToNext || 10000;
  const tierProgress = Math.min(100, Math.round((currentPoints / pointsToNext) * 100));
  const memberSinceRaw = user?.created_at || user?.member_since;
  const memberSince = memberSinceRaw ? new Date(memberSinceRaw).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : null;
  const profileName = (user?.full_name || '').trim();
  const defaultProfileName = defaultNameFromEmail(user?.email);
  const visibleProfileName = profileName || defaultProfileName;
  const username = (user?.username || '').trim();
  const usernameLabel = username ? `@${username}` : 'No username set';
  const showVerifiedBadge = !!badge?.show_badge;
  const nameInputRef = useRef(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(visibleProfileName);
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    if (!editingName) setNameDraft(visibleProfileName);
  }, [editingName, visibleProfileName]);

  useEffect(() => {
    if (!editingName) return;
    const t = setTimeout(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }, 40);
    return () => clearTimeout(t);
  }, [editingName]);

  const startNameEdit = () => {
    setNameDraft(visibleProfileName);
    setEditingName(true);
  };

  const saveProfileName = async () => {
    if (savingName) return;
    const nextName = (nameDraft || '').trim();
    if (!nextName) {
      setNameDraft(visibleProfileName);
      setEditingName(false);
      return;
    }
    if (nextName === profileName) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      await api.patch('/api/auth/profile', { full_name: nextName });
      await checkAuth();
      toast.success('Name updated');
      setEditingName(false);
    } catch (err) {
      setNameDraft(visibleProfileName);
      toast.error(err.response?.data?.detail || 'Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  const copyUsername = async () => {
    if (!username) {
      toast.info('Set a username first');
      onEditProfile?.();
      return;
    }
    try {
      await navigator.clipboard.writeText(username);
      toast.success('Username copied');
    } catch {
      toast.error('Could not copy username');
    }
  };

  const showVerifiedUserToast = (e) => {
    e?.stopPropagation?.();
    tap();
    toast.success('Verified user');
  };

  const [viewAllTiers, setViewAllTiers] = useState(false);

  return (
    <div
      className="player-app fixed inset-0 z-[60] flex flex-col overflow-y-auto"
      style={{ background: 'var(--vl-bg, #000000)', color: '#fff' }}
      data-testid="profile-fullscreen"
    >
      {/* Top bar */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3" style={{ background: 'var(--vl-bg, #000000)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95"
          style={{ background: PROFILE_ACCENT_DIM, border: `1px solid ${PROFILE_ACCENT_BORDER}` }}
          data-testid="profile-fs-close"
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" style={{ color: PROFILE_ACCENT_TEXT }} />
        </button>
        <p className="display text-base font-extrabold tracking-wide" style={{ color: '#fff' }}>My Profile</p>
        <div className="w-10 h-10" aria-hidden="true" />
      </div>

      {/* Hero */}
      <div className="px-5 pt-7 pb-5 flex flex-col items-center text-center">
        <div className="relative">
          <span
            className="w-32 h-32 rounded-full flex items-center justify-center overflow-hidden"
            style={{
              background: avatarUrl ? '#0B0F0C' : `radial-gradient(circle at 30% 25%, #FFFFFF 0%, ${PROFILE_ACCENT_TEXT} 35%, ${PROFILE_ACCENT} 100%)`,
              boxShadow: `0 0 0 4px color-mix(in oklab, ${PROFILE_GOLD} 42%, transparent), 0 0 40px ${PROFILE_ACCENT_GLOW}, inset 0 0 16px rgba(255,255,255,0.3)`,
              border: `3px solid ${PROFILE_GOLD}`
            }}
          >
            {avatarUrl
              ? <SafeImage src={avatarUrl} alt="avatar" className="w-full h-full object-cover" loading="lazy" fallback={<CircleUser className="w-16 h-16" style={{ color: '#fff' }} />} />
              : <CircleUser className="w-16 h-16" style={{ color: '#fff' }} />}
          </span>
          <button
            onClick={() => !uploading && fileInputRef.current?.click()}
            disabled={uploading}
            className="absolute bottom-1 right-1 w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95"
            style={{
              background: PROFILE_ACTION_BG,
              border: '3px solid #0B0618',
              boxShadow: '0 6px 14px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.16)'
            }}
            data-testid="profile-fs-photo-upload"
            title="Change photo"
          >
            {uploading ? <div className="spinner w-4 h-4" /> : <Camera className="w-4 h-4" style={{ color: '#fff' }} />}
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" data-testid="profile-fs-photo-input" />
        </div>
        <div className="flex items-center gap-2 mt-4">
          {editingName ? (
            <div className="relative">
              <input
                ref={nameInputRef}
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={saveProfileName}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                  if (e.key === 'Escape') {
                    setNameDraft(profileName);
                    setEditingName(false);
                  }
                }}
                maxLength={40}
                className="display w-[min(82vw,360px)] rounded-2xl px-4 py-2 pr-12 text-center text-2xl font-extrabold outline-none"
                style={{
                  background: PROFILE_ACCENT_DIM,
                  border: `1.5px solid ${PROFILE_ACCENT_STRONG_BORDER}`,
                  color: '#FFFFFF',
                  boxShadow: `0 0 0 4px ${PROFILE_ACCENT_DIM}`,
                }}
                placeholder="Set your name"
                data-testid="profile-fs-name-input"
              />
              <span
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: PROFILE_ACCENT_TEXT }}
                aria-hidden
              >
                {savingName ? <div className="spinner w-4 h-4" /> : <BadgeCheck className="w-4 h-4" />}
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={startNameEdit}
              className="display text-2xl font-extrabold transition active:scale-[0.98] hover:brightness-110 rounded-xl px-1"
              style={{ color: visibleProfileName ? '#fff' : PROFILE_ACCENT_TEXT }}
              data-testid="profile-fs-name"
              title="Edit name"
            >
              {visibleProfileName || 'Set your name'}
            </button>
          )}
          <button
            onClick={startNameEdit}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full transition active:scale-95 hover:brightness-110"
            style={{
              background: PROFILE_ACCENT_DIM,
              border: `1px solid ${PROFILE_ACCENT_BORDER}`,
              color: '#fff',
              boxShadow: `0 4px 12px ${PROFILE_ACCENT_GLOW}, inset 0 1px 0 rgba(255,255,255,0.1)`
            }}
            data-testid="profile-fs-edit-bottom"
            aria-label="Edit name"
            title="Edit name"
          >
            <Pencil className="w-4 h-4" />
          </button>
        </div>
        <div
          className="mt-2 inline-flex items-center gap-1.5 rounded-full"
          style={{
            background: PROFILE_ACCENT_DIM,
            border: `1px solid ${PROFILE_ACCENT_BORDER}`,
            color: '#000000',
          }}
          data-testid="profile-fs-username-row"
        >
          <button
            type="button"
            onClick={copyUsername}
            className="inline-flex items-center gap-2 py-1.5 pl-3 pr-1.5 rounded-full transition active:scale-[0.98] hover:brightness-110"
            data-testid="profile-fs-username-copy"
            title={username ? 'Copy username' : 'Set username'}
          >
            <span className="text-sm font-bold leading-none">{usernameLabel}</span>
            <Copy className="w-3.5 h-3.5" />
          </button>
          {showVerifiedBadge && (
            <button
              type="button"
              onClick={showVerifiedUserToast}
              className="mr-2 rounded-full transition active:scale-95 hover:brightness-110"
              data-testid="profile-fs-verified-badge"
              title="Verified user"
              aria-label="Verified user"
            >
              <VerifiedUserBadge />
            </button>
          )}
        </div>
        {memberSince && (
          <span
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold"
            style={{ background: PROFILE_ACCENT_DIM, border: `1px solid ${PROFILE_ACCENT_BORDER}`, color: '#000000' }}
            data-testid="profile-fs-member-since"
          >
            <CalendarDays className="w-3 h-3" /> Member Since {memberSince}
          </span>
        )}
      </div>

      {/* Account shortcuts */}
      <div className="px-5 pb-5 max-w-md w-full mx-auto" data-testid="profile-fs-account-shortcuts">
        <div className="mb-4 text-center">
          <h2
            className="text-2xl font-extrabold"
            style={{ color: '#FFFFFF', fontFamily: '"Plus Jakarta Sans", "Inter", system-ui, sans-serif' }}
          >
            Security &amp; Account
          </h2>
          <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.62)' }}>
            Manage your account and keep it secure
          </p>
        </div>

        <SettingsCard
          icon={Mail}
          iconBg={PROFILE_ACCENT_GRADIENT}
          title="Email"
          subtitle={user?.email || 'Add your email'}
          onClick={onEditProfile}
          testid="profile-main-email-card"
        />
        <SettingsCard
          icon={Phone}
          iconBg="linear-gradient(135deg, #38BDF8, #2563EB)"
          iconClassName="w-6 h-6"
          title="Phone Number"
          subtitle={user?.phone_number || `Add your phone to claim $${phoneVerificationReward(user)} Freeplay`}
          onClick={onEditPhone || onEditProfile}
          testid="profile-main-phone-card"
        />

        <p
          className="text-[11px] uppercase tracking-[0.22em] mt-6 mb-2 px-1"
          style={{ color: PROFILE_ACCENT_TEXT, fontWeight: 700 }}
        >
          Account
        </p>

        <SettingsCard
          icon={Lock}
          iconBg={PROFILE_ACCENT_GRADIENT}
          title="Change Password"
          subtitle="Update your password"
          onClick={onResetPassword}
          testid="profile-main-change-password-card"
        />
        <SettingsCard
          icon={LogOut}
          iconBg="linear-gradient(135deg, var(--vl-btn-logout-from, #EC4899), var(--vl-btn-logout-to, #BE185D))"
          title="Logout"
          subtitle="Sign out of your account"
          onClick={onLogout}
          testid="profile-main-logout-card"
        />
      </div>

      {/* View All Tiers modal */}
      {viewAllTiers && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center px-4 pb-4"
          style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(10px)' }}
          onClick={() => setViewAllTiers(false)}
          data-testid="profile-fs-tiers-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rounded-3xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto"
            style={{ background: 'var(--vl-bg, #000000)', border: `1px solid ${PROFILE_ACCENT_BORDER}` }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="display text-xl font-extrabold flex items-center gap-2" style={{ color: '#fff' }}>
                <Crown className="w-5 h-5" style={{ color: '#FCD34D' }} /> All Tiers
              </h3>
              <button onClick={() => setViewAllTiers(false)} className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.06)', color: '#fff' }}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2">
              {[...TIERS].reverse().map((t) => {
                const isCurrent = t.key === currentTier.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => {
                      const adminDoc = tierDocs[t.name.toLowerCase()] || null;
                      setTierDetail({ ...t, adminDoc });
                    }}
                    className="w-full text-left flex items-center gap-3 p-3 rounded-xl transition active:scale-[0.99] hover:brightness-110"
                    style={{
                      background: isCurrent ? PROFILE_ACCENT_DIM : 'rgba(255,255,255,0.03)',
                      border: isCurrent ? `1.5px solid ${PROFILE_ACCENT_BORDER}` : '1px solid rgba(255,255,255,0.06)',
                    }}
                    data-testid={`profile-fs-tier-row-${t.key}`}
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0" style={{ background: t.gradient }}>{t.icon}</div>
                    <div className="flex-1 min-w-0">
                      <p className="display font-extrabold text-base" style={{ color: '#fff' }}>{t.name}</p>
                      <p className="text-xs" style={{ color: '#9CA3AF' }}>Level {t.level}{t.pointsToNext ? ` · ${t.pointsToNext.toLocaleString()} XP to unlock` : ' · Top tier'}</p>
                    </div>
                    {isCurrent && (
                      <span className="display text-[10px] uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: '#FCD34D', color: '#3F2A00' }}>Current</span>
                    )}
                    <ChevronDown className="w-4 h-4 -rotate-90 shrink-0 ml-1" style={{ color: 'rgba(255,255,255,0.4)' }} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Tier detail modal — admin-editable description per tier (Iter 145) */}
      {tierDetail && (
        <div
          className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center px-4 pb-4"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(12px)' }}
          onClick={() => setTierDetail(null)}
          data-testid="profile-fs-tier-detail"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rounded-3xl w-full max-w-md p-6 max-h-[88vh] overflow-y-auto relative"
            style={{ background: 'var(--vl-bg, #000000)', border: `1px solid ${PROFILE_ACCENT_BORDER}` }}
          >
            <button
              onClick={() => setTierDetail(null)}
              className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.06)', color: '#fff' }}
              aria-label="Close tier detail"
              data-testid="profile-fs-tier-detail-close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex flex-col items-center text-center mb-4">
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center text-4xl mb-3"
                style={{ background: tierDetail.gradient, boxShadow: `0 12px 30px ${tierDetail.crystal}55` }}
              >
                {tierDetail.icon}
              </div>
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: PROFILE_ACCENT_TEXT }}>
                Tier · Level {tierDetail.level}
              </p>
              <h3 className="display text-3xl font-extrabold mt-1" style={{ color: '#fff' }}>
                {tierDetail.name}
              </h3>
              {tierDetail.pointsToNext ? (
                <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
                  {tierDetail.pointsToNext.toLocaleString()} XP to unlock
                </p>
              ) : (
                <p className="text-xs mt-1" style={{ color: '#FCD34D' }}>Top tier</p>
              )}
            </div>

            {/* Admin-edited description (falls back to a friendly default).
                Supports inline markdown: **bold**, *italic*, [text](url),
                and \n linebreaks — rendered safely without dangerouslySetInnerHTML. */}
            <div
              className="text-sm sm:text-base leading-relaxed text-center"
              style={{ color: 'rgba(255,255,255,0.85)' }}
              data-testid="profile-fs-tier-detail-description"
            >
              {tierDetail.adminDoc?.description
                ? renderInlineMarkdown(tierDetail.adminDoc.description)
                : `Reach the ${tierDetail.name} tier to unlock exclusive benefits and rewards. Keep playing to climb higher and earn more.`}
            </div>

            {/* Benefits list — prefer admin-edited benefits; fall back to perks.
                Each benefit is parsed for a leading emoji or [shortcode] icon. */}
            {((tierDetail.adminDoc?.benefits?.length || tierDetail.adminDoc?.perks?.length)) && (
              <div className="mt-5 space-y-2">
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color: PROFILE_ACCENT_TEXT }}>
                  Benefits at this tier
                </p>
                {(tierDetail.adminDoc?.benefits?.length ? tierDetail.adminDoc.benefits : tierDetail.adminDoc.perks || []).map((b, i) => (
                  <BenefitRow key={i} raw={b} accent={PROFILE_ACCENT} />
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => setTierDetail(null)}
              className="mt-6 w-full h-11 rounded-2xl display font-extrabold transition active:scale-[0.98] hover:brightness-110"
              style={{
                background: PROFILE_ACCENT_GRADIENT,
                color: 'var(--vl-btn-primary-text, #031A2A)',
                boxShadow: `0 10px 24px ${PROFILE_ACCENT_GLOW}`,
              }}
              data-testid="profile-fs-tier-detail-ack"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Settings — full-screen Security & Account */}
      {settingsOpen && (
        <div
          className="player-app fixed inset-0 z-[70] flex flex-col animate-fade-in-up"
          style={{ background: 'var(--vl-bg, #000000)' }}
          data-testid="profile-fs-settings-panel"
        >
          <div className="relative px-5 pt-5 pb-4">
            <button
              onClick={() => onCloseSettings?.()}
              className="absolute left-5 top-5 w-11 h-11 rounded-full flex items-center justify-center transition active:scale-95"
              style={{ background: PROFILE_ACCENT_DIM, border: `1px solid ${PROFILE_ACCENT_BORDER}` }}
              data-testid="profile-fs-settings-close"
              aria-label="Back"
            >
              <ChevronLeft className="w-5 h-5" style={{ color: PROFILE_ACCENT_TEXT }} />
            </button>
            <div className="flex justify-center pt-1">
              {branding.logo_url ? (
                <SafeImage
                  src={branding.logo_url}
                  alt={branding.brand_name || 'Brand'}
                  className="h-24 w-auto object-contain select-none"
                  draggable={false}
                  loading="lazy"
                  fallback={null}
                />
              ) : (
                <div
                  className="h-24 w-24 rounded-3xl flex items-center justify-center display text-3xl font-extrabold"
                  style={{
                    background: PROFILE_ACCENT_GRADIENT,
                    border: `1px solid ${PROFILE_ACCENT_BORDER}`,
                    color: '#FFFFFF',
                  }}
                >
                  {(branding.brand_name || 'B').charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto pb-10 px-5 max-w-md w-full mx-auto">
            <h1
              className="text-3xl font-extrabold text-center"
              style={{ color: '#FFFFFF', fontFamily: '"Plus Jakarta Sans", "Inter", system-ui, sans-serif' }}
            >
              Security &amp; Account
            </h1>
            <p className="text-center text-sm mt-1.5 mb-6" style={{ color: '#9CA3AF' }}>
              Manage your account and keep it secure
            </p>

            <SettingsCard
              icon={Mail}
              iconBg={PROFILE_ACCENT_GRADIENT}
              title="Email"
              subtitle={user?.email || 'Add your email'}
              onClick={onEditProfile}
              testid="settings-email-card"
            />
            <SettingsCard
              icon={Phone}
              iconBg="linear-gradient(135deg, #38BDF8, #2563EB)"
              iconClassName="w-6 h-6"
              title="Phone Number"
              subtitle={user?.phone_number || `Add your phone to claim $${phoneVerificationReward(user)} Freeplay`}
              onClick={onEditPhone || onEditProfile}
              testid="settings-phone-card"
            />

            <p
              className="text-[11px] uppercase tracking-[0.22em] mt-6 mb-2 px-1"
              style={{ color: PROFILE_ACCENT_TEXT, fontWeight: 700 }}
            >
              Account
            </p>

            <SettingsCard
              icon={Lock}
              iconBg={PROFILE_ACCENT_GRADIENT}
              title="Change Password"
              subtitle="Update your password"
              onClick={onResetPassword}
              testid="settings-change-password-card"
            />
            <SettingsCard
              icon={LogOut}
              iconBg="linear-gradient(135deg, var(--vl-btn-logout-from, #EC4899), var(--vl-btn-logout-to, #BE185D))"
              title="Logout"
              subtitle="Sign out of your account"
              onClick={onLogout}
              testid="settings-logout-card"
            />
          </div>
        </div>
      )}
    </div>
  );
}
